<template>
  <b-modal
    id="service-system-add-others-modal"
    cancel-variant="outline-secondary"
    :ok-title="$t('chat.assign')"
    :cancel-title="$t('button.close')"
    centered
    no-close-on-backdrop
    :title="$t('button.add_others')"
    :ok-disabled="disabledAdd"
    @ok="handleOk"
  >
    <validation-observer
      ref="addOthersForm"
      #default="{ invalid }"
    >
      <!-- form -->
      <n-form-confirmation
        ref="formConfirmation"
        key="assignOperatorForm"
        :message="$t('general.operators_added')"
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.addOthersForm"
        :disabled="invalid"
        @submit="submit"
      >
        <label class="d-block">
          {{ $t('general.operators') }}
        </label>
        <n-async-multi-select
          v-model="data.assignedOperatorIds"
          repository="operatorForService"
          selection-key="id"
          selection-label="name"
          :placeholder="$t('general.select_operators')"
          always-fresh
          :query="{
            isResetService: true,
            serviceType: data.serviceType,
            serviceCategory: data.serviceCategory,
            projectId: data.projectId
          }"
          :must-excluded-ids="mustExcludedIds"
          :selectable="(option) => !option.isDisabledBreakTime"
          :immediate="false"
        >
          <template #option="item">
            {{ item.name }}
            <div
              v-if="item.totalServiceCap"
              class="float-right mr-2"
              style="display: flex; align-items: center !important;"
            >
              <div
                v-if="item.isDisabledBreakTime"
                class="mr-1"
                style="margin-bottom: 2px;"
              >
                <feather-icon
                  v-b-tooltip.hover.left
                  icon="CoffeeIcon"
                  class="text-danger"
                  :title="$t('general.on_break_time')"
                />
              </div>
              <span>{{ `${item.totalServiceWork}/${item.totalServiceCap}` }}</span>
            </div>
          </template></n-async-multi-select>
      </n-form-confirmation>
    </validation-observer>

    <template #modal-ok>
      <b-spinner
        v-if="loading"
        small
        label="Spinning"
      />
      <span v-else>
        {{ $t('button.add') }}
      </span>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import NAsyncMultiSelect from '@/components/NAsyncMultiSelect.vue'
import useJwt from '@/auth/jwt/useJwt'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import FormInput from './assignOperatorFormInput'

const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  components: {
    BModal,
    BSpinner,
    NFormConfirmation,
    NAsyncMultiSelect,
  },
  props: {
    activeChannel: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
    mustExcludedIds: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      data: {
        assignedOperatorIds: [],
        serviceSystemId: '',
        departmentId: '',
        serviceType: '',
        serviceCategory: '',
        projectId: '',
      },
      initOperatorIds: [],
      operatorNames: [],
      key: 1,
      initialOperatorIds: [], // Add this to track initial value
    }
  },
  computed: {
    disabledAdd() {
      return (this.data.assignedOperatorIds || [])?.length === 0
    },
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
  },
  methods: {
    getLabel(option) {
      return option.name || ''
    },
    show(serviceSystem) {
      this.key += 1
      this.data = {
        serviceSystemId: serviceSystem.id,
        assignedOperatorIds: serviceSystem.assignedOperatorIds,
        departmentId: serviceSystem.departmentId,
        serviceType: serviceSystem.serviceType?.code,
        serviceCategory: serviceSystem.serviceCategory?.code,
        projectId: serviceSystem.projectId,
      }
      this.initOperatorIds = serviceSystem.assignedOperatorIds // Store initial value
      this.operatorNames = serviceSystem.assignedOperators
      this.initialOperatorIds = serviceSystem.assignedOperatorIds // Store initial value
      this.$bvModal.show('service-system-add-others-modal')
    },
    hide() {
      this.data = {}
      this.$bvModal.hide('service-system-add-others-modal')
    },
    onChangeOperator(data) {
      this.operatorName = data?.name
    },
    handleOk(bvModal) {
      bvModal.preventDefault()
      if (this.loading) {
        return
      }

      this.$refs.addOthersForm.validate().then(success => {
        if (success) {
          this.$refs.formConfirmation.confirm()
        }
      })
    },
    submit() {
      this.loading = true
      ServiceSystemRepository.addOthers(
        {
          status: 'pending',
          serviceSystemId: this.data.serviceSystemId,
          operatorIds: this.data.assignedOperatorIds,
        },
      )
        .then(() => {
          this.hide()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('general.added'),
              icon: 'TrashIcon',
              variant: 'success',
              text: this.$t('general.helperAdded'),
            },
          })
          this.$emit('success', this.data.assignedOperatorIds)
        })
        .catch(error => {
          if (error.response?.status === 422) {
            this.$refs.addOthersForm.setErrors(
                error.response?.data?.validate,
            )
          }
        })
        .then(() => {
          this.loading = false
        })
    },
  },
  setup() {
    const formInput = [...FormInput]

    return {
      formInput,
    }
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
